// Angular imports
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

// Rxjs import,
import { Subject } from 'rxjs';

// Design System imports
import { ToastService } from '@ciranet/ui';

// Service imports
import { AuthService } from '../auth.service';

// Constant imports
import { MessageInterval } from '../../_constants/messages-intervals';

// Model imports
import { MessageBoxInfo } from '../../_models/message-box-info';

@Injectable({
  providedIn: 'root'
})
export class ServerErrorService {
  private readonly _subjectErrorMessageChanged: Subject<string> = new Subject();
  private readonly _subjectMessageChanged: Subject<MessageBoxInfo> =
    new Subject();

  constructor(
    private readonly authService: AuthService,
    private readonly toastService: ToastService
  ) {}

  // Need to change defaultErrorMsgInterval
  displayError(
    error: HttpErrorResponse,
    prefixMessage = '',
    displayTime = MessageInterval.defaultErrorMsgInterval
  ) {
    if (prefixMessage !== '') {
      prefixMessage += '. ';
    }

    if (!navigator.onLine) {
      // browser offline
      this.displayMessage(
        'No internet connection. Please check and try again',
        'error'
      );
    } else if (error.status === 400) {
      // bad request
      this.process400Error(error);
    } else if (error.status === 401) {
      // unauthorized
      this.process401Error(error);
    } else if (error.status === 403) {
      // forbidden
      this.process403Error(error, prefixMessage);
    } else if (error.status === 404) {
      // not found
      this.process404Error(error, prefixMessage);
    } else if (error.status === 409) {
      this.process409Error(error, prefixMessage);
    } else if (error.status === 500) {
      this.process500Error(error, prefixMessage);
    } else if (error.status === 503) {
      this.updateSubjectErrorMessage(
        `Our servers are experiencing high traffic or undergoing maintenance. Please try again shortly.`
      );
    } else {
      this.processOtherError(error, prefixMessage);
    }
  }

  displayInfo(
    info: string,
    displayTime: number = MessageInterval.defaultErrorMsgInterval
  ) {
    this.toastService.info(info, { duration: displayTime });
  }

  displayMessage(
    message: string,
    type: string,
    displayTime = MessageInterval.defaultErrorMsgInterval
  ) {
    const options = { duration: displayTime };
    switch (type) {
      case 'success':
        this.toastService.success(message, options);
        break;
      case 'warning':
        this.toastService.warning(message, options);
        break;
      case 'info':
        this.toastService.info(message, options);
        break;
      default:
        this.toastService.error(message, options);
        break;
    }
  }

  private getErrorMessage(
    error: HttpErrorResponse,
    defaultMessage: string
  ): string {
    let message: any = '';
    if (!!error) {
      message = error.error || error.message || '';
      if (typeof message !== 'string') {
        if (!!message.byteLength) {
          const enc = new TextDecoder('utf-8');
          const arr = new Uint8Array(message);
          const msg = enc.decode(arr);
          try {
            const json = JSON.parse(msg);
            message = json.message || json.error;
          } catch {
            message = msg;
          }
        } else {
          message = '';
        }
      }
    }
    return !!message.length ? message : defaultMessage;
  }

  private process400Error(error: HttpErrorResponse): void {
    let errors = [];
    let modelState: any;
    if (!!error.error) {
      if (Object.prototype.hasOwnProperty.call(error.error, 'modelState')) {
        modelState = error.error.modelState;
      } else if (
        Object.prototype.hasOwnProperty.call(error.error, 'ModelState')
      ) {
        modelState = error.error.ModelState;
      }
      if (!!modelState) {
        for (const property in modelState) {
          if (Object.prototype.hasOwnProperty.call(modelState, property)) {
            errors = errors.concat(modelState[property]);
          }
        }
      } else if (!!error.error.message) {
        errors.push(error.error.message);
      } else if (!!error.error.byteLength) {
        const enc = new TextDecoder('utf-8');
        const arr = new Uint8Array(error.error);
        const msg = enc.decode(arr);
        try {
          const json = JSON.parse(msg);
          error.error.message = json.message || json.error;
        } catch {
          error.error.message = msg;
        }
        errors.push(error.error.message);
      } else {
        errors.push(error.error);
      }
    }
    this.updateSubjectErrorMessage(errors.join());
  }

  private process401Error(error: HttpErrorResponse): void {
    const message = this.getErrorMessage(
      error,
      'Application session is expired! Please login to continue.'
    );
    this.updateSubjectErrorMessage(message);
    this.authService.logOut();
  }

  private process403Error(
    error: HttpErrorResponse,
    suffixMessage: string
  ): void {
    const message = this.getErrorMessage(error, `Access Forbidden. `);
    this.updateSubjectErrorMessage(message + suffixMessage);
  }

  private process404Error(
    error: HttpErrorResponse,
    suffixMessage: string
  ): void {
    const message = this.getErrorMessage(error, `Resource Not Found. `);
    this.updateSubjectErrorMessage(message + suffixMessage);
  }

  private process409Error(
    error: HttpErrorResponse,
    suffixMessage: string
  ): void {
    const message = this.getErrorMessage(error, `Server Data Conflict. `);
    if (!!error && !!error.error) {
      const errorMessage = String.fromCharCode.apply(null, [
        ...new Uint8Array(error.error)
      ]);
      if (!!errorMessage) {
        this.updateSubjectErrorMessage(errorMessage);
      } else {
        this.updateSubjectErrorMessage(message + suffixMessage);
      }
    } else {
      this.updateSubjectErrorMessage(message + suffixMessage);
    }
  }

  private process500Error(
    error: HttpErrorResponse,
    suffixMessage: string
  ): void {
    const message = this.getErrorMessage(error, `Internal server error. `);
    this.updateSubjectErrorMessage(message + suffixMessage);
  }

  private processOtherError(
    error: HttpErrorResponse,
    prefixMessage: string
  ): void {
    const message = this.getErrorMessage(error, `Error occurred. `);
    this.displayMessage(prefixMessage + message, 'error');
  }

  updateSubjectErrorMessage(message: string): void {
    this._subjectErrorMessageChanged.next(message);
  }

  getSubjectErrorMessage(): Subject<string> {
    return this._subjectErrorMessageChanged;
  }

  updateSubjectMessage(info: MessageBoxInfo): void {
    this._subjectMessageChanged.next(info);
  }

  getSubjectMessage(): Subject<MessageBoxInfo> {
    return this._subjectMessageChanged;
  }
}
